import React, { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionTemplate,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";
import { Nav } from "@/components/fina/Nav";
import { Footer } from "@/components/fina/Footer";
import { Cursor } from "@/components/fina/Cursor";
import { ScrollProgress } from "@/components/fina/ScrollProgress";
import { EngineeringGrid } from "@/components/fina/EngineeringGrid";
import { Magnetic } from "@/components/fina/Magnetic";
import { useLenisSmooth } from "@/lib/lenisSmooth";
import { usePageTitle } from "@/lib/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "/brand/capability-welding.jpg";
const WORK_IMG =
  "https://customer-assets.emergentagent.com/job_engineer-journey-2/artifacts/ms923ji0_WhatsApp%20Image%202026-01-15%20at%2015.41.26.jpeg";

const CareersHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const headlineY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const headlineLS = useMotionTemplate`calc(-0.04em + ${useTransform(
    scrollYProgress,
    [0, 1],
    [0, -0.5]
  )}px)`;

  return (
    <section
      ref={ref}
      data-testid="careers-hero"
      className="relative w-full bg-[#0a0a0c]"
      style={{ height: "110vh" }}
    >
      <div className="sticky top-0 h-[100svh] w-full overflow-hidden">
        <motion.div
          style={{ scale, y }}
          className="absolute inset-0 will-change-transform brushed-steel"
        >
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.55) contrast(1.08)" }}
          />
        </motion.div>

        {/* Cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/95 pointer-events-none" />
        <div className="absolute inset-0 vignette pointer-events-none" />

        {/* Light sweep */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none overflow-hidden mix-blend-screen"
        >
          <span
            className="absolute -top-1/3 left-0 h-[160%] w-[40%]"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 45%, rgba(255,180,120,0.14) 50%, rgba(255,255,255,0.06) 55%, transparent 100%)",
              animation: "lightsweep 10s ease-in-out infinite",
              animationDelay: "1.5s",
            }}
          />
        </div>

        <span className="absolute top-24 left-6 md:left-12 text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono">
          F-CAREERS · 2026
        </span>
        <span className="absolute top-24 right-6 md:right-12 text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono">
          Lincolnshire · UK
        </span>

        <motion.div
          style={{ y: headlineY }}
          className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-32 px-6 md:px-12 max-w-7xl mx-auto"
        >
          <p className="overline mb-6" data-testid="careers-hero-overline">
            Fina Engineering &nbsp;/&nbsp; Careers
          </p>
          <motion.h1
            style={{ letterSpacing: headlineLS }}
            className="font-heading font-medium text-white text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.98]"
            data-testid="careers-hero-headline"
          >
            Built by people who care
            <br />
            <span className="italic font-normal text-[#FF5722]">
              about the details.
            </span>
          </motion.h1>

          <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <p className="font-body text-white/70 max-w-lg text-base md:text-lg leading-relaxed">
              Join a team working on high-spec stainless fabrication for food
              and pharmaceutical industries across the UK.
            </p>
            <Magnetic strength={0.25}>
              <a
                href="#careers-cta"
                data-testid="careers-hero-cta"
                className="cta-glow inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-black font-medium text-sm hover:bg-white/90 transition self-start md:self-end"
              >
                Start a conversation
                <ArrowDown size={16} />
              </a>
            </Magnetic>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Standard = () => (
  <section
    data-testid="careers-standard"
    className="relative bg-[#09090b] py-28 md:py-40 overflow-hidden ambient-depth"
  >
    <EngineeringGrid gridOpacity={0.045} />

    <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <p className="overline mb-6">— The standard</p>
        <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]">
          Not all fabrication
          <br />
          is the same.
        </h2>
      </div>
      <motion.p
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-7 text-white/65 font-body text-lg md:text-xl leading-relaxed max-w-2xl"
      >
        We work on projects where precision matters — stainless fabrication for
        food and pharmaceutical environments, built to a standard, not just a
        price.
      </motion.p>
    </div>
  </section>
);

const PEOPLE = [
  "Take pride in their work.",
  "Pay attention to detail.",
  "Understand quality matters.",
  "Want to work on better projects.",
];

const People = () => (
  <section
    data-testid="careers-people"
    className="relative bg-[#09090b] py-28 md:py-40 overflow-hidden ambient-depth"
  >
    <div className="relative max-w-6xl mx-auto px-6 md:px-12">
      <p className="overline mb-6">— The type of people</p>
      <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] max-w-3xl">
        We don&apos;t hire often.
      </h2>
      <p className="mt-8 text-white/60 font-body text-lg max-w-2xl">
        But when we do, we look for people who —
      </p>

      <ul className="mt-12 md:mt-16 space-y-4 md:space-y-5 max-w-3xl">
        {PEOPLE.map((line, i) => (
          <motion.li
            key={line}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
              duration: 0.7,
              delay: i * 0.12,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative flex items-start gap-5 border-b border-white/10 pb-5 md:pb-6"
            data-testid={`careers-people-item-${i}`}
          >
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF5722] pt-2 w-10">
              0{i + 1}
            </span>
            <span className="font-heading text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
              {line}
            </span>
          </motion.li>
        ))}
      </ul>
    </div>
  </section>
);

const Work = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  return (
    <section
      ref={ref}
      data-testid="careers-work"
      className="relative h-[110vh] bg-black overflow-hidden"
    >
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={WORK_IMG}
          alt="Hygienic stainless pipework installation"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/85" />

      <div className="sticky top-0 h-screen flex items-end md:items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 md:grid-cols-12 gap-8 pb-24 md:pb-0">
          <div className="md:col-span-7">
            <p className="overline mb-6">— The work</p>
            <h2
              className="font-heading text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em]"
              data-testid="careers-work-headline"
            >
              Work that
              <br />
              <span className="italic font-normal text-[#FF5722]">
                speaks for itself.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:ml-auto md:mt-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="glass rounded-2xl p-7 md:p-8 max-w-md"
            >
              <p className="text-white/80 font-body text-base md:text-lg leading-relaxed">
                From hygienic pipework to complex stainless fabrications, our
                work supports industries that can&apos;t afford mistakes.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CareersForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [cv, setCv] = useState(null); // { filename, base64, sizeKB }
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onField = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("CV must be under 5 MB");
      e.target.value = "";
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const b64 = String(r.result).split(",")[1] || "";
      setCv({
        filename: file.name,
        base64: b64,
        sizeKB: Math.round(file.size / 1024),
      });
    };
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/careers`, {
        ...form,
        cv_filename: cv?.filename || null,
        cv_content_base64: cv?.base64 || null,
      });
      setDone(true);
      toast.success("Message received — we'll be in touch if it's a fit.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="careers-cta"
      data-testid="careers-cta"
      className="relative bg-[#050507] py-32 md:py-48 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(60,28,12,0.5) 0%, rgba(5,5,7,1) 65%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center mb-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="overline mb-6 inline-block"
        >
          — An open door
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="font-heading text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] max-w-5xl mx-auto"
          data-testid="careers-cta-headline"
        >
          Think you&apos;d
          <span className="italic font-normal text-[#FF5722]"> fit in?</span>
        </motion.h2>
        <p className="mt-6 text-white/60 font-body text-base md:text-lg max-w-xl mx-auto">
          We&apos;re always open to hearing from the right people.
        </p>
      </div>

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-3xl mx-auto px-6 md:px-12"
        data-testid="careers-form"
      >
        <div className="bg-[#111113] border border-white/10 rounded-2xl p-8 md:p-12">
          {done ? (
            <div className="flex flex-col items-start gap-5 py-10">
              <CheckCircle2 className="text-[#FF5722]" size={44} />
              <h3 className="font-heading text-white text-2xl md:text-3xl">
                Thank you — message received.
              </h3>
              <p className="text-white/60 font-body max-w-md">
                If it&apos;s a fit, a member of the Fina team will reach out.
                We take our time with this.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Your name"
                value={form.name}
                onChange={onField("name")}
                testid="careers-field-name"
                required
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={onField("email")}
                testid="careers-field-email"
                required
              />
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
                  A few words about you
                  <span className="text-[#FF5722] ml-1">*</span>
                </label>
                <textarea
                  data-testid="careers-field-message"
                  rows={5}
                  value={form.message}
                  onChange={onField("message")}
                  required
                  placeholder="Where you've worked, what you've welded, what you're looking for."
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#FF5722] text-white font-body outline-none transition-colors pb-3 resize-none placeholder:text-white/30"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
                  CV / Portfolio (optional)
                </label>
                {cv ? (
                  <div className="flex items-center justify-between bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Paperclip size={16} className="text-white/70 shrink-0" />
                      <span className="text-sm text-white/85 truncate">
                        {cv.filename}
                      </span>
                      <span className="text-xs text-white/40 shrink-0">
                        {cv.sizeKB} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid="careers-cv-remove"
                      onClick={() => setCv(null)}
                      className="text-white/50 hover:text-white transition"
                      aria-label="Remove CV"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="careers-cv"
                    data-testid="careers-cv-label"
                    className="flex items-center justify-between border border-dashed border-white/15 hover:border-white/30 rounded-xl px-4 py-4 cursor-pointer transition-colors"
                  >
                    <span className="text-sm text-white/60 font-body">
                      PDF or DOC, up to 5 MB
                    </span>
                    <span className="inline-flex items-center gap-2 text-sm text-white/80">
                      <Paperclip size={15} />
                      Attach
                    </span>
                    <input
                      id="careers-cv"
                      data-testid="careers-field-cv"
                      type="file"
                      accept=".pdf,.doc,.docx,.rtf"
                      onChange={onFile}
                      className="sr-only"
                    />
                  </label>
                )}
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2">
                <p className="text-xs text-white/40 font-body max-w-sm">
                  We&apos;ll only use this to get in touch — nothing else.
                </p>
                <Magnetic strength={0.25}>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="careers-submit"
                    className="cta-glow inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#FF5722] hover:bg-[#F4511E] text-black font-medium text-sm transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Start a conversation <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </Magnetic>
              </div>
            </div>
          )}
        </div>
      </motion.form>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, testid, required }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.25em] text-white/50 mb-3">
      {label}
      {required && <span className="text-[#FF5722] ml-1">*</span>}
    </label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full h-12 bg-transparent border-b border-white/15 focus:border-[#FF5722] text-white font-body outline-none transition-colors"
    />
  </div>
);

export default function Careers() {
  useLenisSmooth();
  usePageTitle("Careers — Fina Engineering");
  useNavigate(); // ensures router context; no-op otherwise
  return (
    <div
      className="relative bg-[#09090b] text-white min-h-screen"
      data-testid="careers-page"
    >
      <Cursor />
      <ScrollProgress />
      <Nav />
      <main>
        <CareersHero />
        <Standard />
        <People />
        <Work />
        <CareersForm />
      </main>
      <Footer />
    </div>
  );
}
